import { debounce } from 'lodash-es';
import { useEffect, useState } from 'react';

import { ScreenBreakpoint } from '@pflegia/types/common/enums/screen-breakpoint.enum';

export const DEBOUNCE_DIMENSION_CHANGE_INTERVAL = 200;
export const MOBILE_BREAKPOINT_IN_PIXELS = 768;
export const TABLET_BREAKPOINT_IN_PIXELS = 1024;

const getBreakpoint = (width: number): ScreenBreakpoint => {
  if (width <= MOBILE_BREAKPOINT_IN_PIXELS) return ScreenBreakpoint.Mobile;
  if (width <= TABLET_BREAKPOINT_IN_PIXELS) return ScreenBreakpoint.Tablet;
  return ScreenBreakpoint.Desktop;
};

/**
 * Returns the active screen breakpoint (enum: ScreenBreakpoint)
 */
export const useGetActiveScreenBreakpoint = () => {
  const initialWidth = typeof window === 'undefined' ? TABLET_BREAKPOINT_IN_PIXELS : window.innerWidth;
  const [activeBreakpoint, setActiveBreakpoint] = useState<ScreenBreakpoint>(getBreakpoint(initialWidth));

  useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = getBreakpoint(window.innerWidth);
      setActiveBreakpoint(newBreakpoint);
    };

    const debouncedResize = debounce(handleResize, DEBOUNCE_DIMENSION_CHANGE_INTERVAL);
    window.addEventListener('resize', debouncedResize);
    debouncedResize();

    return () => {
      debouncedResize.cancel();
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return {
    activeBreakpoint,
    isMobile: activeBreakpoint === ScreenBreakpoint.Mobile,
    isTablet: activeBreakpoint === ScreenBreakpoint.Tablet,
    isDesktop: activeBreakpoint === ScreenBreakpoint.Desktop,
  };
};
